/**
 * The Creative Performance table's column model (change `add-creative-performance-table-view`).
 *
 * One array, not a headless table library. Sorting, filtering and paging are all done server-side
 * and their state is the URL, so a client-side table engine would contribute a column model and a
 * header renderer while fighting `useCreativePerformanceUrlState` for ownership of the same state.
 * This file IS the column model; the header renderer is forty lines.
 *
 * **The read-only/editable split is the point of the file.** A column's `kind` says where its values
 * come from, and that alone decides whether the table renders an edit affordance:
 *
 *   - `metric` — derived from ClickHouse sums, recomputed on every request. Nothing to write to.
 *   - `provider` — mirrored from Meta by the entity sync, overwritten on the next sweep.
 *   - `select` / `text` — the buyer's own annotation, the only user-writable data on a row.
 *
 * `isEditableKind` is the single place that mapping lives, so a column added later cannot
 * accidentally become editable by having the right-looking fields.
 */
import type { ICreativePerformanceRow } from '@/api/creative-intelligence/get-creative-performance-query'
import { toneForTableColumn, type IMetricToneResult } from '@/features/creative-performance/creative-performance-tone'
import {
    MARKETING_TABLE_HEADER_HEIGHT,
    MARKETING_TABLE_ROW_HEIGHT,
} from '@/features/marketing-table/utils/marketing-table-types'
import {
    CREATIVE_PERFORMANCE_DIMENSION_FIELD_IDS,
    isCreativePerformanceFieldId,
    type ICreativePerformanceDimensionFieldId,
    type ICreativePerformanceField,
    type ICreativePerformanceFieldId,
} from '@/features/creative-performance/creative-performance-analysis-state'
import type { ICreativePerformanceFieldCategory } from '@/features/creative-performance/creative-performance-field-categories'
import { CREATIVE_METRIC_CATALOG } from 'shared'
import { dimensionCellText } from '@/features/creative-performance/creative-performance-contributions'
import {
    GRID_METRIC_BY_KEY,
    outcomeMetricRename,
    type ICardMetricEntry,
    type IGridMetricKey,
    type IGridOutcomeModel,
} from '@/features/creative-performance/creative-performance-metrics'
import { CREATIVE_TYPE_LABEL } from '@/features/creative-performance/creative-performance-view-helpers'
// The advertiser's OWN event name, returned verbatim. A third humaniser here lowercased the tail,
// so `Purchase_B2B_SKU` became "Purchase b2b sku" in the table and stayed verbatim in the detail
// panel — and matching the column back to Events Manager by eye is the whole point of the key.
import { resultTypeSingularLabel } from '@/features/agents/agent-panel/agent-panel-employees/channel-insights-result-type-label'

/** Every column id the table knows. The identity column is not in here — it is never configurable. */
export type ICreativeTableColumnId =
    | IGridMetricKey
    | 'purchases'
    | 'results'
    | 'adCount'
    | 'aov'
    | 'cpm'
    | 'outboundCtr'
    | 'hookRate'
    | 'costPerAddToCart'
    | 'addToCartRate'
    | 'landingPageViewRate'
    | 'conversionRate'
    | 'revenuePerVisitor'
    | 'daysActive'
    | 'outboundClicks'
    | 'frequency'
    | 'spendShareOfAdSet'
    | 'spendShareOfCampaign'
    | 'spendShareOfAccount'
    | 'cpaVsTarget'
    | 'outboundCtrChange'
    | 'cpmChange'
    | 'cpaChange'
    | 'cpaVsOwnPast'
    | Exclude<ICreativePerformanceDimensionFieldId, 'name'>
    // Retained in the union so an older shared link naming them still parses and degrades by
    // dropping an id it no longer knows, rather than discarding the whole column configuration.
    | 'annotationStatus'
    | 'annotationNote'

/**
 * Where a column's values come from. Drives the header's type glyph and, through `isEditableKind`,
 * whether cells render an edit affordance at all.
 */
export type ICreativeTableColumnKind = 'metric' | 'provider' | 'count' | 'select' | 'text'

/** Only the buyer's own annotation is writable. Everything else is a fact from somewhere else. */
export function isEditableKind(kind: ICreativeTableColumnKind): boolean {
    return kind === 'select' || kind === 'text'
}

export interface ICreativeTableColumn {
    id: ICreativeTableColumnId
    label: string
    kind: ICreativeTableColumnKind
    /** The catalog category this column's field belongs to — the Properties popover's sections. */
    category: ICreativePerformanceFieldCategory
    /**
     * The column's width in pixels. In a definition this is the DEFAULT; `resolveTableColumns`
     * overlays the user's resize on top of it. The row is a CSS grid, so widths are the grid
     * template.
     */
    width: number
    align: 'left' | 'right'
    /**
     * The server-side sort key, or `null` when this column cannot be sorted over the whole result
     * set. A `null` here renders the header's sort control disabled — never a sort of the loaded
     * page dressed up as a sort of the set.
     */
    sortKey: ICreativePerformanceFieldId | null
    /** Display string for the cell. */
    format: (row: ICreativePerformanceRow, currency: string) => string
    /**
     * The row's own verdict for this metric, `null` when this row cannot be judged, and absent
     * entirely on a column that carries no verdict at all. Never a function of the loaded set.
     */
    tone?: (row: ICreativePerformanceRow) => IMetricToneResult | null
}

/** The annotation vocabulary, in the order a creative moves through it. */
// Re-exported from `shared`, where the vocabulary now lives so the metric catalog can use it
// without this module and the grid metrics importing each other. Every existing import resolves
// through here unchanged. A re-export does not bind the names LOCALLY, hence the import beside it.
import { ANNOTATION_STATUS_LABEL, type IAnnotationStatus } from 'shared'
import { DASH, formatCountValue } from '@/features/creative-performance/creative-metric-format'

export { ANNOTATION_STATUSES, ANNOTATION_STATUS_LABEL, type IAnnotationStatus } from 'shared'

/**
 * Pill colours for the status vocabulary. Deliberately NOT the metric tone palette: a status is a
 * label the user chose, not a verdict the data produced, and colouring them the same would read as
 * the tool agreeing (or disagreeing) with the buyer.
 */
export const ANNOTATION_STATUS_STYLE: Record<IAnnotationStatus, string> = {
    WINNER: 'bg-emerald-50 text-emerald-700',
    SCALING: 'bg-sky-50 text-sky-700',
    TESTING: 'bg-amber-50 text-amber-700',
    LOSER: 'bg-red-50 text-red-700',
    RETIRED: 'bg-[#F0EEEA] text-[#746C64]',
}

/**
 * Every column the table can show, in the order the Properties popover lists them.
 *
 * `costPerResult` is wider than a money column needs because it carries LABELLED values on a creative
 * whose ads span differing objectives — "Website Start Trial €127.04, Website Subscription €34.24"
 * rather than one figure. It sorts on its primary objective (see the field registry). The two
 * annotation columns have `sortKey: null` because annotations are joined onto the page AFTER it is
 * sliced, so any sort would be a sort of the loaded rows presented as a sort of the result set.
 */
/**
 * Per-column layout, which is the ONLY thing about a column that is not a property of the metric.
 *
 * Width is a function of the label's length and the value's shape on THIS surface; a card renders
 * the same metric with no width at all. Anything absent here takes the default.
 */
const COLUMN_WIDTHS: Record<string, number> = {
    spend: 120,
    roas: 100,
    purchaseValue: 140,
    cpa: 140,
    costPerResult: 220,
    impressions: 120,
    purchases: 110,
    results: 110,
    adCount: 80,
    aov: 160,
    cpm: 110,
    outboundCtr: 130,
    hookRate: 110,
    holdRate: 110,
    costPerAddToCart: 150,
    addToCartRate: 130,
    landingPageViews: 150,
    landingPageViewRate: 170,
    conversionRate: 130,
    revenuePerVisitor: 160,
    daysActive: 110,
    outboundClicks: 130,
    frequency: 110,
    spendShareOfAdSet: 160,
    spendShareOfCampaign: 170,
    spendShareOfAccount: 170,
    cpaVsTarget: 120,
    outboundCtrChange: 170,
    cpmChange: 120,
    cpaChange: 120,
    cpaVsOwnPast: 140,
}

const DEFAULT_COLUMN_WIDTH = 140

/**
 * Metrics the SERVER cannot order the result set by, whatever the catalog says about them.
 *
 * Empty today. `costPerResult` used to be here — it is withheld per row on a mixed-objective
 * creative — but its registry reader now falls back to the PRIMARY objective's own cost, so every
 * row has a real value to order by. The set stays because the catalog is wider than the server's
 * sortable field list, and a metric that gains a column before it gains a sort belongs here rather
 * than offering a header control that ranks the loaded page and presents it as a ranking of the set.
 */
const UNSORTABLE_METRICS = new Set<string>()

/**
 * Every metric column, projected from the shared catalog.
 *
 * This file used to declare its columns by hand, in two shapes (`metricColumn` delegated formatting
 * and tone to the grid's definitions; `funnelColumn` carried its own formatter). That split is
 * exactly how the table came to render a metric differently from the card showing the same number,
 * and how a metric promoted onto the row could reach the cards while silently dashing here. Both
 * shapes are gone: a column is a catalog entry plus a width.
 *
 * Sortability is resolved twice, deliberately. The static value below is what the table opens with;
 * `creativeTableColumnsFromCatalog` then overlays the SERVER's own answer once the field catalog
 * loads, because only the server knows what it can order.
 */
function catalogMetricColumn(entry: ICardMetricEntry): ICreativeTableColumn {
    const definition = GRID_METRIC_BY_KEY[entry.key]
    // The guard has to narrow at the assignment, not through a boolean: the catalog is wider than
    // the server's field ids, and a column claiming a sort key the server does not know would rank
    // the loaded page and present it as a ranking of the whole set.
    const sortKey =
        !UNSORTABLE_METRICS.has(entry.key) && isCreativePerformanceFieldId(entry.key) ? entry.key : null
    return {
        id: entry.key,
        label: entry.label,
        kind: 'metric',
        category: 'metric',
        width: COLUMN_WIDTHS[entry.key] ?? DEFAULT_COLUMN_WIDTH,
        align: 'right',
        sortKey,
        format: definition.format,
        // Central dispatcher: grid ids → absolute model, everything else → relative/advisory policy.
        tone: toneForTableColumn(entry.key),
    }
}

/**
 * Every column the table can show, in the order the Properties popover lists them.
 *
 * `deliveryStatus` is the one metric the catalog declares that is NOT projected: a creative's ads can
 * sit in different states, and the table's own cell collapses that into one phrase (or "Mixed")
 * rather than naming the highest-spend member's status as though it were the creative's.
 */
export const CREATIVE_TABLE_COLUMNS: ICreativeTableColumn[] = [
    ...CREATIVE_METRIC_CATALOG.filter((entry) => entry.key !== 'deliveryStatus').map((entry) =>
        catalogMetricColumn(entry as ICardMetricEntry)
    ),
    {
        id: 'creativeType',
        label: 'Type',
        kind: 'provider',
        category: 'creative',
        width: 110,
        align: 'left',
        sortKey: null,
        format: (row) => (row.creativeType ? (CREATIVE_TYPE_LABEL[row.creativeType] ?? row.creativeType) : '—'),
    },
    {
        id: 'deliveryStatus',
        label: 'Delivery',
        kind: 'provider',
        category: 'setup',
        width: 130,
        align: 'left',
        sortKey: null,
        format: (row) => formatDeliveryStatus(row),
    },
    // The two annotation columns — the buyer's own vocabulary, and the table's only writable
    // cells. Status leads the default set (see `DEFAULT_TABLE_COLUMN_IDS`); Note stays opt-in
    // behind Properties, because a mostly-empty wide text column by default is noise.
    {
        id: 'annotationStatus',
        label: 'Status',
        kind: 'select',
        category: 'creative',
        width: 130,
        align: 'left',
        // Annotations are joined AFTER the page slice, so any sort would be a sort of the loaded
        // rows presented as a sort of the result set — the same refusal costPerResult makes.
        sortKey: null,
        format: (row) => {
            const status = row.annotationStatus as IAnnotationStatus | null | undefined
            return status ? ANNOTATION_STATUS_LABEL[status] : '—'
        },
    },
    {
        id: 'annotationNote',
        label: 'Note',
        kind: 'text',
        category: 'creative',
        width: 240,
        align: 'left',
        sortKey: null,
        format: (row) => row.annotationNote ?? '—',
    },
]

export const CREATIVE_TABLE_COLUMN_BY_ID = Object.fromEntries(
    CREATIVE_TABLE_COLUMNS.map((column) => [column.id, column])
) as Record<ICreativeTableColumnId, ICreativeTableColumn>

export const CREATIVE_TABLE_COLUMN_IDS: ICreativeTableColumnId[] = [
    ...CREATIVE_TABLE_COLUMNS.map((column) => column.id),
    ...CREATIVE_PERFORMANCE_DIMENSION_FIELD_IDS.filter(
        (field): field is Exclude<ICreativePerformanceDimensionFieldId, 'name'> => field !== 'name'
    ),
].filter((id, index, ids) => ids.indexOf(id) === index)

/**
 * What the table opens on. Status leads: it is the buyer's own verdict beside the identity —
 * Linear's shape — and it is the one column that teaches, by hover affordance alone, that cells
 * here can be edited.
 */
export const DEFAULT_TABLE_COLUMN_IDS: ICreativeTableColumnId[] = [
    'annotationStatus',
    'spend',
    'roas',
    'purchaseValue',
    'cpa',
    'impressions',
    'adCount',
]

/**
 * The table's default columns for an account whose outcome carries no money — the table's half of
 * the same rule `defaultMetricKeysFor` applies to the grid's cards.
 *
 * Three of the seven defaults above are purchase-shaped, so a table opened on an advertiser that
 * never sells led with three empty columns. `cpa` divides by the ACCOUNT's own terminal event, so it
 * is fillable for any objective; `outcomeMetricRename` renames its header to that event.
 */
export const NON_VALUE_TABLE_COLUMN_IDS: ICreativeTableColumnId[] = [
    'annotationStatus',
    'spend',
    'results',
    'cpa',
    'impressions',
    'adCount',
]

/**
 * What the table opens on, given what the account measures.
 *
 * With no model this returns `DEFAULT_TABLE_COLUMN_IDS` unchanged, so a first paint and a stale
 * cached response both render exactly what they render today.
 */
export function defaultTableColumnIdsFor(model?: IGridOutcomeModel | null): ICreativeTableColumnId[] {
    if (!model || model.hasValue) return DEFAULT_TABLE_COLUMN_IDS
    return NON_VALUE_TABLE_COLUMN_IDS
}

/** The pinned identity column — thumbnail, name, ad count. Never hidden, never reordered. */
/**
 * The identity column's width is user-resizable like any other, but the column itself is not
 * selectable — it is always present and always first. It therefore needs an id to be addressable in
 * the width map WITHOUT being a member of `CREATIVE_TABLE_COLUMN_IDS`, or it would show up in the
 * Properties popover as something you could turn off.
 */
export const IDENTITY_COLUMN_ID = 'creative'

export const DEFAULT_IDENTITY_COLUMN_WIDTH = 320

/**
 * Resize bounds. The floor is what keeps a column from being dragged to nothing and stranding its
 * header — a zero-width column is unrecoverable without editing the URL by hand. The identity
 * column gets a higher floor because it carries a 28px thumbnail, a name and an ad count, and below
 * ~180px that collapses into an unreadable ellipsis.
 */
export const MIN_COLUMN_WIDTH = 64
export const MIN_IDENTITY_COLUMN_WIDTH = 180
export const MAX_COLUMN_WIDTH = 720

/** A resizable column's id — every selectable column, plus the always-present identity column. */
export type ICreativeTableWidthKey = ICreativeTableColumnId | typeof IDENTITY_COLUMN_ID

/** Per-column width overrides. A column absent from the map is at its default width. */
export type ICreativeTableColumnWidths = Partial<Record<ICreativeTableWidthKey, number>>

/**
 * Hold a width inside this column's bounds. Applied on every path that can set one — the drag, the
 * keyboard nudge and the URL parser — so a hand-edited `?columns=spend:5` degrades to the floor
 * rather than rendering a column nobody can grab again.
 */
export function clampColumnWidth(key: ICreativeTableWidthKey, width: number): number {
    const min = key === IDENTITY_COLUMN_ID ? MIN_IDENTITY_COLUMN_WIDTH : MIN_COLUMN_WIDTH
    return Math.max(min, Math.min(MAX_COLUMN_WIDTH, Math.round(width)))
}

/** The default width for any resizable column, including the identity column. */
export function defaultColumnWidth(key: ICreativeTableWidthKey): number {
    if (key === IDENTITY_COLUMN_ID) return DEFAULT_IDENTITY_COLUMN_WIDTH
    return CREATIVE_TABLE_COLUMN_BY_ID[key]?.width ?? MIN_COLUMN_WIDTH
}

/** The width to render for one column — the user's resize if there is one, else the default. */
export function resolveColumnWidth(key: ICreativeTableWidthKey, widths: ICreativeTableColumnWidths): number {
    const override = widths[key]
    return override === undefined ? defaultColumnWidth(key) : clampColumnWidth(key, override)
}
/**
 * A body row is tall enough to carry a legible poster. The creative IS the row's identity here —
 * a name like `260814_Static_v3` says nothing a buyer can act on, so the thumbnail has to be big
 * enough to recognise the ad at a glance rather than merely mark that one exists.
 */
/** Alias the marketing-table row height so creative-performance callers stay stable. */
export const TABLE_ROW_HEIGHT = MARKETING_TABLE_ROW_HEIGHT

/**
 * Alias the marketing-table header height so creative-performance callers stay stable.
 * Sticky group offsets and the skeleton share this value with the marketing-table engine.
 */
export const TABLE_HEADER_HEIGHT = MARKETING_TABLE_HEADER_HEIGHT

/** The identity cell's poster edge, in CSS pixels. Kept here so the row height stays derived from it. */
export const TABLE_THUMBNAIL_SIZE = 48

/**
 * One glyph per column kind, shown before the header label — the same vocabulary Notion, Airtable
 * and Linear use, so a user reads the column's nature without hovering a cell. `Σ` and `⟳` are the
 * whole read-only signal in the header; the absence of a hover affordance is the signal in the body.
 */
export const COLUMN_KIND_GLYPH: Record<ICreativeTableColumnKind, string> = {
    metric: 'Σ',
    provider: '⟳',
    count: '#',
    select: '⊙',
    text: '≡',
}

export const COLUMN_KIND_TITLE: Record<ICreativeTableColumnKind, string> = {
    metric: 'Calculated from reported metrics — read-only',
    provider: 'Synced from the ad platform — read-only',
    count: 'Counted from the underlying ads — read-only',
    select: 'Your own label — click a cell to change it',
    text: 'Your own note — click a cell to edit it',
}

/**
 * Meta reports a list of delivery reasons per ad; a creative collapses several ads. Show the first
 * and disclose that there are more, rather than concatenating them into an unreadable cell or
 * picking one silently.
 */
function formatDeliveryStatus(row: ICreativePerformanceRow): string {
    const statuses = (row.deliveryStatuses ?? []).filter(Boolean)
    if (statuses.length === 0) return row.effectiveStatus ? titleCase(row.effectiveStatus) : '—'
    const [first, ...rest] = statuses
    return rest.length > 0 ? `${titleCase(first)} +${rest.length}` : titleCase(first)
}

function titleCase(value: string): string {
    return value
        .toLowerCase()
        .split('_')
        .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
        .join(' ')
}

/**
 * Resolve an ordered list of column ids to definitions, dropping ids that no longer exist. A saved
 * view or a hand-edited link from before a column was removed degrades to the columns it named that
 * still exist, rather than rendering an empty table.
 */
export function resolveTableColumns(
    ids: readonly ICreativeTableColumnId[],
    widths: ICreativeTableColumnWidths = {},
    availableColumns: readonly ICreativeTableColumn[] = CREATIVE_TABLE_COLUMNS
): ICreativeTableColumn[] {
    const available = new Map(availableColumns.map((column) => [column.id, column]))
    return (
        ids
            .map((id) => available.get(id))
            .filter((column): column is ICreativeTableColumn => Boolean(column))
            // The resize is overlaid here rather than mutated into the definition, so the definitions
            // stay the single source of DEFAULT widths and "reset to default" is just dropping the key.
            .map((column) => {
                const override = widths[column.id]
                return override === undefined ? column : { ...column, width: clampColumnWidth(column.id, override) }
            })
    )
}

/**
 * Default width per provider column, by how much text the value actually carries. Copy fields hold a
 * whole ad body, so they open wide enough to be worth showing at all; a bucket label is two words.
 */
const PROVIDER_COLUMN_WIDTHS: Partial<Record<Exclude<ICreativePerformanceDimensionFieldId, 'name'>, number>> = {
    primaryText: 320,
    headline: 240,
    landingPage: 220,
    uniqueCreative: 200,
    campaign: 180,
    adName: 180,
    adSet: 180,
    landingPageDomain: 170,
    launchWeek: 150,
    format: 110,
    videoLength: 120,
    launchDate: 120,
    launchMonth: 110,
}

function providerColumnWidth(id: Exclude<ICreativePerformanceDimensionFieldId, 'name'>) {
    return PROVIDER_COLUMN_WIDTHS[id] ?? 150
}

/**
 * The prefix that marks a column as belonging to ONE account's own conversion event.
 *
 * `IGridMetricKey` stays a closed union over the shared catalog, deliberately: its COMPLEMENT
 * defines the groupable dimensions, so widening it to `string` would make every dimension id also a
 * valid metric id and the reclassification that follows is silent — the metric simply renders as a
 * dimension nobody asked for. A prefixed id cannot collide with a catalog key, and a reader of a
 * saved view can see at a glance that the column is account-scoped.
 */
export const CUSTOM_EVENT_COLUMN_PREFIX = 'event:'

/**
 * Columns for the advertiser's OWN conversions, named from the rows that carry them.
 *
 * Derived from the loaded rows rather than from a static list, because a custom conversion exists
 * for one account and not the next — that is what makes it custom. An account firing none produces
 * no columns at all, rather than a set of empty ones.
 *
 * A saved view naming an event the current account does not fire therefore finds no column and
 * DROPS it, which is the same rule the per-account event metrics already follow.
 */
export function customEventColumns(
    rows: readonly { customEventMetrics?: Record<string, { count?: number }> }[]
): ICreativeTableColumn[] {
    const seen = new Map<string, string>()
    for (const row of rows) {
        for (const actionType of Object.keys(row.customEventMetrics ?? {})) {
            if (!seen.has(actionType)) seen.set(actionType, resultTypeSingularLabel(actionType))
        }
    }
    return [...seen.entries()].map(([actionType, label]) => ({
        id: `${CUSTOM_EVENT_COLUMN_PREFIX}${actionType}` as ICreativeTableColumnId,
        label,
        kind: 'metric',
        category: 'metric',
        width: DEFAULT_COLUMN_WIDTH,
        align: 'right',
        // The server cannot order by a key it does not publish as a field id, and a column claiming
        // a sort key the server does not know would rank the loaded page and present it as a
        // ranking of the whole set.
        sortKey: null,
        format: (row) => {
            const values = (row as { customEventMetrics?: Record<string, { count?: number }> })
                .customEventMetrics?.[actionType]
            // Absent means the account does not report this event at all; a measured zero is a
            // number and reads as one. Formatted through the shared count formatter so this column
            // cannot read `1,284` beside a catalog count reading `1.3K`.
            return values?.count === undefined ? DASH : formatCountValue(values.count)
        },
    })) as ICreativeTableColumn[]
}

/**
 * Build opt-in provider columns from the API catalog while preserving the current metric defaults.
 *
 * `model` renames the five outcome columns to the account's own terminal event. Both label sources
 * here — the metric catalog and the server's field catalog — are static per-metric strings written
 * for an e-commerce advertiser, so without this a trial account's header reads `Cost / purchase`
 * over a column of costs per trial, and disagrees with the card chip beside it.
 */
export function creativeTableColumnsFromCatalog(
    fields: readonly ICreativePerformanceField[],
    model?: IGridOutcomeModel | null
) {
    const catalog = new Map(fields.map((field) => [field.id, field]))
    const base = CREATIVE_TABLE_COLUMNS.map((column) => {
        const field = catalog.get(column.id as ICreativePerformanceFieldId)
        const named = field
            ? { ...column, label: field.label, category: field.category, sortKey: field.sortable ? field.id : null }
            : column
        const renamed = outcomeMetricRename(named.id, model)
        return renamed ? { ...named, label: renamed } : named
    })
    const existing = new Set(base.map((column) => column.id))
    const providers: ICreativeTableColumn[] = fields
        .filter(
            (
                field
            ): field is ICreativePerformanceField & { id: Exclude<ICreativePerformanceDimensionFieldId, 'name'> } =>
                field.column &&
                CREATIVE_PERFORMANCE_DIMENSION_FIELD_IDS.includes(field.id as ICreativePerformanceDimensionFieldId) &&
                field.id !== 'name' &&
                !existing.has(field.id as ICreativeTableColumnId)
        )
        .map((field) => {
            // Bound OUTSIDE the cell closure. The metric field ids are now derived from the shared
            // catalog, so the field-id union is much wider than the dimension one, and reading
            // `field.id` from inside the closure resolves against that wider union instead of the
            // narrowing the filter above already established.
            // The `includes` guard in the filter above IS the runtime guarantee; the cast carries it
            // to the type level. It cannot be inferred: the metric field ids are now derived from the
            // shared catalog, so `ICreativePerformanceFieldId` is much wider than the dimension union
            // and the intersection does not collapse to it.
            const dimensionId = field.id as Exclude<ICreativePerformanceDimensionFieldId, 'name'>
            return {
                id: dimensionId,
                label: field.label,
                kind: 'provider',
                category: field.category,
                width: providerColumnWidth(dimensionId),
                align: 'left',
                sortKey: field.sortable ? dimensionId : null,
                format: (row) => dimensionCellText(row.dimensions?.[dimensionId]),
            } satisfies ICreativeTableColumn
        })
    return [...base, ...providers]
}
